import { useMemo } from 'react';

import { getComponentTimeline } from '@/features/content/hooks/useComponentTimeline';
import type {
  ComponentPageData,
  RawQuestionSettings,
} from '@/features/content/useComponent';
import {
  describeAttempts,
  type SectionAttempts,
} from '@/features/questions/attemptSettings';

export interface ControlPanelRowStatus {
  actionLabel: string;
  isVisible: boolean;
  text: string;
}

export interface ControlPanelStatusModel {
  homework: ControlPanelRowStatus & {
    isAssignmentLocked: boolean;
    showAssignAction: boolean;
  };
  preLecture: ControlPanelRowStatus & {
    isAssignmentLocked: boolean;
    showAssignAction: boolean;
  };
  visibility: ControlPanelRowStatus;
}

type AssignmentTypeLabel =
  | 'assigned consistently'
  | 'assigned randomly'
  | 'assigned to all students'
  | 'chosen manually';

const ASSIGNMENT_TYPE_LABELS: Record<string, AssignmentTypeLabel> = {
  '0': 'assigned to all students',
  '1': 'assigned to all students',
  '2': 'assigned consistently',
  '3': 'assigned randomly',
  '4': 'chosen manually',
  ALL: 'assigned to all students',
  FIXED: 'assigned consistently',
  RANDOM: 'assigned randomly',
  UNKNOWN: 'assigned to all students',
  USER_DEFINED: 'chosen manually',
};

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function getTimestamp(value: unknown) {
  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatDateTime(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function isVisibleAt(timestamp: string | undefined, now: number) {
  const parsedTimestamp = getTimestamp(timestamp);

  if (parsedTimestamp === undefined) {
    return false;
  }

  return now >= parsedTimestamp;
}

export function getAssignmentTypeLabel(
  settings: RawQuestionSettings | undefined,
): AssignmentTypeLabel {
  const key =
    typeof settings?.type === 'number'
      ? String(settings.type)
      : normalizeString(settings?.type)?.toUpperCase() ?? 'UNKNOWN';

  return ASSIGNMENT_TYPE_LABELS[key] ?? 'assigned to all students';
}

export function getDueDateStatus(
  timestamp: string | undefined,
  subject: 'Homework' | 'Pre-lecture',
  now = Date.now(),
  isClosed = false,
) {
  const parsedTimestamp = getTimestamp(timestamp);

  if (parsedTimestamp === undefined) {
    return isClosed ? `${subject} is closed.` : 'No due date set.';
  }

  return now >= parsedTimestamp
    ? `${subject} closed on ${formatDateTime(parsedTimestamp)}.`
    : `${subject} is due on ${formatDateTime(parsedTimestamp)}.`;
}

export function getVisibilityStatusText(
  component: ComponentPageData,
  now = Date.now(),
) {
  const timeline = getComponentTimeline(component.rawComponent, now);
  const contentVisible = timeline.isContentVisible;
  const homeworkVisible =
    component.controlPanel.hasHomeworkQuestionSection &&
    timeline.isHomeworkVisible;
  const preLectureVisible =
    component.controlPanel.hasPreLectureQuestionSection &&
    timeline.isPreLectureVisible;

  if (!contentVisible && !homeworkVisible && !preLectureVisible) {
    return "This section is not visible to students. You can change this in 'Visibility Settings'.";
  }

  const visibleParts: string[] = [];
  const hiddenParts: string[] = [];

  if (contentVisible) {
    visibleParts.push('section');
  }

  if (preLectureVisible) {
    visibleParts.push('pre-lecture questions');
  } else if (component.controlPanel.hasPreLectureQuestionSection) {
    hiddenParts.push(
      timeline.preLectureStartDate
        ? `Pre-lecture will be visible after ${formatDateTime(
            timeline.preLectureStartDate.getTime(),
          )}.`
        : 'Pre-lecture questions are still hidden.',
    );
  }

  if (homeworkVisible) {
    visibleParts.push('homework questions');
  } else if (component.controlPanel.hasHomeworkQuestionSection) {
    hiddenParts.push(
      timeline.homeworkStartDate
        ? `Homework will be visible after ${formatDateTime(
            timeline.homeworkStartDate.getTime(),
          )}.`
        : 'Homework questions are still hidden.',
    );
  }

  const visibleText =
    visibleParts.length === 1 && visibleParts[0] === 'section'
      ? 'This section is visible to students.'
      : visibleParts.length > 0
        ? `This ${visibleParts.join(' and ')} are visible to students.`
        : 'This section is partially visible to students.';

  return [visibleText, ...hiddenParts].join(' ');
}

export function getHomeworkStatusText(
  component: ComponentPageData,
  now = Date.now(),
) {
  const total = component.controlPanel.homeworkQuestionOptions.length;
  const assigned = component.controlPanel.homeworkAssignedIndexes.length;
  const assignmentLabel = getAssignmentTypeLabel(component.controlPanel.questionSettings);
  const timeline = getComponentTimeline(component.rawComponent, now);

  return withAttempts(
    `${assigned} of ${total} homework questions ${assignmentLabel}. ${getDueDateStatus(
      component.controlPanel.homeworkEndTimeUtcMilli,
      'Homework',
      now,
      timeline.isHomeworkClosed,
    )}`,
    component.controlPanel.attempts?.homework,
  );
}

// The attempts in effect end the row, so an instructor can see them without
// opening Question Settings (issue #54).
function withAttempts(text: string, attempts: SectionAttempts | undefined) {
  return attempts ? `${text} ${describeAttempts(attempts)}` : text;
}

export function getPreLectureStatusText(
  component: ComponentPageData,
  now = Date.now(),
) {
  const total = component.controlPanel.preLectureQuestionOptions.length;
  const assigned = component.controlPanel.preLectureAssignedIndexes.length;

  if (total === 0) {
    return 'No pre-lecture questions assigned yet. Move homework questions into pre-lecture to schedule them separately.';
  }

  const assignmentLabel = getAssignmentTypeLabel(
    component.controlPanel.preLectureQuestionSettings,
  );
  const timeline = getComponentTimeline(component.rawComponent, now);

  return withAttempts(
    `${assigned} of ${total} pre-lecture questions ${assignmentLabel}. ${getDueDateStatus(
      component.controlPanel.preLectureEndTimeUtcMilli,
      'Pre-lecture',
      now,
      timeline.isPreLectureClosed,
    )}`,
    component.controlPanel.attempts?.preLecture,
  );
}

export function getControlPanelStatus(
  component: ComponentPageData,
  now = Date.now(),
): ControlPanelStatusModel {
  const timeline = getComponentTimeline(component.rawComponent, now);
  const homeworkLocked = timeline.isHomeworkVisible || timeline.isHomeworkClosed;
  const preLectureLocked =
    timeline.isPreLectureVisible || timeline.isPreLectureClosed;
  const homeworkAssignmentType = normalizeString(
    component.controlPanel.questionSettings?.type,
  );
  const isHomeworkSubset =
    homeworkAssignmentType?.toUpperCase() === 'USER_DEFINED' ||
    component.controlPanel.questionSettings?.type === 4;
  const hasPreLectureQuestions =
    component.controlPanel.preLectureQuestionOptions.length > 0;

  return {
    homework: {
      actionLabel: 'Homework',
      isAssignmentLocked: homeworkLocked,
      isVisible: component.controlPanel.hasHomeworkQuestionSection,
      showAssignAction:
        component.controlPanel.hasHomeworkQuestionSection && !homeworkLocked,
      text: getHomeworkStatusText(component, now),
    },
    preLecture: {
      actionLabel: 'Pre-lecture',
      isAssignmentLocked: preLectureLocked,
      isVisible:
        component.controlPanel.hasHomeworkQuestionSection ||
        component.controlPanel.hasPreLectureQuestionSection,
      showAssignAction:
        component.controlPanel.hasHomeworkQuestionSection &&
        !hasPreLectureQuestions &&
        !isHomeworkSubset &&
        !preLectureLocked,
      text: getPreLectureStatusText(component, now),
    },
    visibility: {
      actionLabel: 'Visibility',
      isVisible: true,
      text: getVisibilityStatusText(component, now),
    },
  };
}

export function useControlPanelStatus(
  component: ComponentPageData,
  now = Date.now(),
) {
  return useMemo(() => getControlPanelStatus(component, now), [component, now]);
}
